import * as _ from "lodash";
import { Spread, Excel } from "../../grapecity";
import { WorkSheetBase } from "./workSheetBase";
import * as SPIRIT from "../../SPIRIT";


/**
 * Spread基底クラス
 */
export abstract class WorkBookBase {

    static readonly DATA_KEY = 'SPIRIT-workbook';

    /**
     * コンテナID
     */
    public container: any;

    /**
     * モード
     */
    public mode: SPIRIT.constant.GridMode;

    /**
     * 行ヘッダーを右クリックした際にコンテキストメニュー「コピー」「貼付け」を非表示にする #4328
     * 非活性列があるSpreadはこのプロパティをtrueにする
     */
    public doHideCopyAndPastingContextMenuAtRowHeader: boolean = false;

    /**
     * Spread option
     * 全て項目は下記
     * allowUserDragMerge<boolean>:ユーザーがセルをドラッグしてマージできるかどうか（オプション）。
     * allowUserDragDrop<boolean>:ユーザーが範囲データをドラッグ＆ドロップできるかどうかを指定します（オプション）。
     * allowUserDragFill<boolean>:ユーザーが範囲をドラッグフィルできるかどうかを指定します（オプション）。
     * allowUserZoom<boolean>:［Ctrl］キーを押しながらマウスホイールを回すことによって表示をズームできるかどうかを指定します（オプション）。
     * allowUserResize<boolean>:ユーザーが行および列のサイズを変更できるかどうかを指定します（オプション）。
     * allowUndo<boolean>:ユーザーが編集をアンドゥできるかどうかを指定します（オプション）。
     * allowSheetReorder<boolean>:ユーザーがSpreadコンポーネントのシートの順序を変更できるかどうかを指定します（オプション）。
     * allowContextMenu<boolean>:ユーザーが組み込みのコンテキストメニューを開くことができるかどうか（オプション）。
     * allowCopyPasteExcelStyle<boolean>:ユーザーがSpread.SheetsのスタイルをコピーしてExcelに貼り付ける、またはExcelのスタイルをコピーしてSpread.Sheetsに貼り付けることができるかどうかを指定します（オプション）。
     * allowExtendPasteRange<boolean>:貼り付け範囲が足りない場合に貼り付け範囲を拡張するかどうかを指定します（オプション）。
     * copyPasteHeaderOptions<GC.Spread.Sheets.CopyPasteHeaderOptions>:データをコピーまたは貼り付けるときに含めるヘッダを指定します（オプション）。
     * defaultDragFillType<GC.Spread.Sheets.Fill.AutoFillType>:デフォルトのフィルタイプ（オプション）。
     * showDragFillSmartTag<boolean>:ドラッグフィルダイアログを表示するかどうかを指定します（オプション）。
     * showHorizontalScrollbar<boolean>:水平スクロールバーを表示するかどうかを指定します（オプション）。
     * showVerticalScrollbar<boolean>:垂直スクロールバーを表示するかどうかを指定します（オプション）。
     * scrollbarShowMax<boolean>:表示されるスクロールバーがシートの全体の列数と行数に基づくかどうかを指定します（オプション）。
     * scrollbarMaxAlign<boolean>:スクロールバーがアクティブシートの最後の行および列に揃うかどうかを指定します（オプション）。
     * tabStripVisible<boolean>:シートタブストリップを表示するかどうかを指定します（オプション）。
     * tabStripRatio<number>:水平スクロールバーの幅全体に対する割合として表されたタブストリップの幅（オプション）。
     * tabEditable<boolean>:ユーザーがシートタブストリップを編集できるかどうかを指定します（オプション）。
     * newTabVisible<boolean>:ユーザーが新しいシートを挿入するための特殊なタブをスプレッドシートに表示するかどうかを指定します（オプション）。
     * tabNavigationVisible<boolean>:シートタブナビゲーションを表示するかどうかを指定します（オプション）。
     * cutCopyIndicatorVisible<boolean>:選択した項目をコピーまたは切り取るときにインジケーターを表示するかどうかを指定します（オプション）。
     * cutCopyIndicatorBorderColor<string>:ユーザーが選択した項目をコピーまたは切り取るときに表示されるインジケーターの境界線の色（オプション）。
     * backColor<string>:Spreadコンポーネントの背景色を表す色文字列（オプション）。"red"、"#FFFF00"、"rgb(255,0,0)"、"Accent 5"など。
     * backgroundImage<string>:Spreadコンポーネントの背景画像（オプション）。
     * backgroundImageLayout<GC.Spread.Sheets.ImageLayout>:Spreadコンポーネントの背景画像レイアウト（オプション）。
     * grayAreaBackColor<string>:灰色領域の背景色を表す色文字列（オプション）。"red"、"#FFFF00"、"rgb(255,0,0)"、"Accent 5"など。
     * showResizeTip<GC.Spread.Sheets.ShowResizeTip>:サイズ変更チップの表示方法を指定します（オプション）。
     * showDragDropTip<boolean>:ドラッグ＆ドロップチップを表示するかどうかを指定します（オプション）。
     * showDragFillTip<boolean>:ドラッグフィルチップを表示するかどうかを指定します（オプション）。
     * showScrollTip<GC.Spread.Sheets.ShowScrollTip>:スクロールチップの表示方法を指定します（オプション）。
     * scrollIgnoreHidden<boolean>:スクロールバーが非表示の行または列を無視するかどうかを指定します（オプション）。
     * highlightInvalidData<boolean>:無効なデータを強調表示するかどうかを指定します（オプション）。
     * useTouchLayout<boolean>:Spreadコンポーネントを表示するためにタッチレイアウトを使用するかどうかを指定します（オプション）。
     * hideSelection<boolean>:Spreadコンポーネントがフォーカスを持たないときに選択範囲をハイライト表示するかどうかを指定します（オプション）。
     * resizeZeroIndicator<GC.Spread.Sheets.ResizeZeroIndicator>:行または列のサイズがゼロに変更されたときの描画ポリシー（オプション）。
     * allowUserEditFormula<boolean>:ユーザーがスプレッドシートのセルに設定された数式を編集できるかどうかを指定します（オプション）。
     * enableFormulaTextbox<boolean>:スプレッドシートで数式テキストボックスを有効にするかどうかを指定します（オプション）。
     * autoFitType<GC.Spread.Sheets.AutoFitType>:セル内またはセルおよびヘッダ内に収まるように内容を書式設定するかどうかを指定します（オプション）。
     * referenceStyle<GC.Spread.Sheets.ReferenceStyle>:このシート上のセル数式でのセルおよび範囲参照のスタイル（オプション）。
     */
    public options: {};

    /**
     * Spread.Sheets.Workbook(Raw)
     */
    public rawSpread: Spread.Sheets.Workbook;

    /**
     * 内包Sheet
     */
    public sheets: WorkSheetBase[];

    // #4119
    /**
     * 拡張WorkSheet
     * SpreadJSのWorkSheetを使いたい場合にセットする
     * */
    public extentSheets?: Spread.Sheets.Worksheet[];

    /**
     * 中枢処理
     */
    private promisePool: Promise<any>[] = [];


    /**
     * 活性状態
     * @param disabled
     */
    private _disabled: boolean = false;
    /**
     * 活性状態
     * @param disabled
     */
    get disabled(): boolean {
        return this._disabled;
    }
    /**
     * 活性制御
     * @param disabled
     */
    set disabled(disabled: boolean) {
        this._disabled = disabled;
        this.sheets.forEach(s => {
            s.disabled = this._disabled;
        });
    }


    /**
     * delegate
     * シートの値の変更を通知する
     */
    onNotifyValueChanged?: (ins: WorkSheetBase, sheetName: string, colmunName: string, newValue: any) => void;

    /**
     * delegate
     * #5736
     */
    onNotifySorting?: (ins: WorkSheetBase, args: any) => void;

    /**
     * 
     * @param mode
     * @param editAuthList
     */
    constructor(mode: SPIRIT.constant.GridMode) {
        this.mode = mode;
        this.sheets = [];
    }



    /**
     * spreadメソッドを返却
     * @returns spreadメソッドｌ
     */
    public getSpread() {
        return this.rawSpread;
    }

    /**
     * sheetメソッドを返却
     * @returns spreadメソッドｌ
     */
    public getSheet(sheetName: string): WorkSheetBase {
        const sheet = this.sheets.find(s => s.sheetName === sheetName);
        return sheet || null;
    }

    /**
     * sheetメソッドを返却
     * @returns spreadメソッドｌ
     */
    public getActiveSheet(): WorkSheetBase {
        const sheet = this.sheets.find(s => s.isActive);
        return sheet || null;
    }

    /**
     * extentSheetメソッドを返却
     * @returns workSheetメソッドｌ
     */
    public getExtentSheet(sheetName: string): Spread.Sheets.Worksheet {
        const sheet = this.extentSheets.find(s => s.name() === sheetName);
        return sheet || null;
    }

    /**
     * Spreadのデフォルト設定（全て項目は下記）公開用(#5636)
     * 
     *    sheetCount  ：  number  ：  シートの数（オプション）。
     *    font  ：  string  ：  タブストリップのフォント（オプション）。
     *    allowUserDragDrop  ：  boolean  ：  ユーザーが範囲データをドラッグ＆ドロップできるかどうかを指定します（オプション）。
     *    allowUserDragFill  ：  boolean  ：  ユーザーが範囲をドラッグフィルできるかどうかを指定します（オプション）。
     *    allowUserZoom  ：  boolean  ：  ［Ctrl］キーを押しながらマウスホイールを回すことによって表示をズームできるかどうかを指定します（オプション）。
     *    allowUserResize  ：  boolean  ：  ユーザーが行および列のサイズを変更できるかどうかを指定します（オプション）。
     *    allowUndo  ：  boolean  ：  ユーザーが編集をアンドゥできるかどうかを指定します（オプション）。
     *    allowSheetReorder  ：  boolean  ：  ユーザーがSpreadコンポーネントのシートの順序を変更できるかどうかを指定します（オプション）。
     *    allowCopyPasteExcelStyle  ：  boolean  ：  ユーザーがSpread.SheetsのスタイルをコピーしてExcelに貼り付ける、またはExcelのスタイルをコピーしてSpread.Sheetsに貼り付けることができるかどうかを指定します（オプション）。
     *    allowExtendPasteRange  ：  boolean  ：  貼り付け範囲が足りない場合に貼り付け範囲を拡張するかどうかを指定します（オプション）。
     *    copyPasteHeaderOptions  ：  GC.Spread.Sheets.CopyPasteHeaderOptions  ：  データをコピーまたは貼り付けるときに含めるヘッダを指定します（オプション）。
     *    defaultDragFillType  ：  GC.Spread.Sheets.Fill.AutoFillType  ：  デフォルトのフィルタイプ（オプション）。
     *    showDragFillSmartTag  ：  boolean  ：  ドラッグフィルダイアログを表示するかどうかを指定します（オプション）。
     *    showHorizontalScrollbar  ：  boolean  ：  水平スクロールバーを表示するかどうかを指定します（オプション）。
     *    showVerticalScrollbar  ：  boolean  ：  垂直スクロールバーを表示するかどうかを指定します（オプション）。
     *    scrollbarShowMax  ：  boolean  ：  表示されるスクロールバーがシートの全体の列数と行数に基づくかどうかを指定します（オプション）。
     *    scrollbarMaxAlign  ：  boolean  ：  スクロールバーがアクティブシートの最後の行および列に揃うかどうかを指定します（オプション）。
     *    tabStripVisible  ：  boolean  ：  シートタブストリップを表示するかどうかを指定します（オプション）。
     *    tabStripRatio  ：  number  ：  水平スクロールバーの幅全体に対する割合として表されたタブストリップの幅（オプション）。
     *    tabEditable  ：  boolean  ：  ユーザーがシートタブストリップを編集できるかどうかを指定します（オプション）。
     *    newTabVisible  ：  boolean  ：  ユーザーが新しいシートを挿入するための特殊なタブをスプレッドシートに表示するかどうかを指定します（オプション）。
     *    tabNavigationVisible  ：  boolean  ：  シートタブナビゲーションを表示するかどうかを指定します（オプション）。
     *    cutCopyIndicatorVisible  ：  boolean  ：  選択した項目をコピーまたは切り取るときにインジケーターを表示するかどうかを指定します（オプション）。
     *    cutCopyIndicatorBorderColor  ：  string  ：  ユーザーが選択した項目をコピーまたは切り取るときに表示されるインジケーターの境界線の色（オプション）。
     *    backColor  ：  string  ：  Spreadコンポーネントの背景色を表す色文字列（オプション）。"red"、"#FFFF00"、"rgb(255,0,0)"、"Accent 5"など。
     *    backgroundImage  ：  string  ：  Spreadコンポーネントの背景画像（オプション）。
     *    backgroundImageLayout  ：  GC.Spread.Sheets.ImageLayout  ：  Spreadコンポーネントの背景画像レイアウト（オプション）。
     *    grayAreaBackColor  ：  string  ：  灰色領域の背景色を表す色文字列（オプション）。"red"、"#FFFF00"、"rgb(255,0,0)"、"Accent 5"など。
     *    showResizeTip  ：  GC.Spread.Sheets.ShowResizeTip  ：  サイズ変更チップの表示方法を指定します（オプション）。
     *    showDragDropTip  ：  boolean  ：  ドラッグ＆ドロップチップを表示するかどうかを指定します（オプション）。
     *    showDragFillTip  ：  boolean  ：  ドラッグフィルチップを表示するかどうかを指定します（オプション）。
     *    showScrollTip  ：  GC.Spread.Sheets.ShowScrollTip  ：  スクロールチップの表示方法を指定します（オプション）。
     *    scrollIgnoreHidden  ：  boolean  ：  スクロールバーが非表示の行または列を無視するかどうかを指定します（オプション）。
     *    highlightInvalidData  ：  boolean  ：  無効なデータを強調表示するかどうかを指定します（オプション）。
     *    useTouchLayout  ：  boolean  ：  Spreadコンポーネントを表示するためにタッチレイアウトを使用するかどうかを指定します（オプション）。
     *    hideSelection  ：  boolean  ：  Spreadコンポーネントがフォーカスを持たないときに選択範囲をハイライト表示するかどうかを指定します（オプション）。
     *    resizeZeroIndicator  ：  GC.Spread.Sheets.ResizeZeroIndicator  ：  行または列のサイズがゼロに変更されたときの描画ポリシー（オプション）。
     *    allowUserEditFormula  ：  boolean  ：  ユーザーがスプレッドシートのセルに設定された数式を編集できるかどうかを指定します（オプション）。
     *    enableFormulaTextbox  ：  boolean  ：  スプレッドシートで数式テキストボックスを有効にするかどうかを指定します（オプション）。
     *    autoFitType  ：  GC.Spread.Sheets.AutoFitType  ：  セル内またはセルおよびヘッダ内に収まるように内容を書式設定するかどうかを指定します（オプション）。
     *    referenceStyle  ：  GC.Spread.Sheets.ReferenceStyle  ：  このシート上のセル数式でのセルおよび範囲参照のスタイル（オプション）。
     *    rowResizeMode  ：  GC.Spread.Sheets.ResizeMode  ：  行のサイズを変更する方法を指定します（オプション）。
     *    columnResizeMode  ：  GC.Spread.Sheets.ResizeMode  ：  列のサイズを変更する方法を指定します（オプション）。
     *    scrollByPixel  ：  boolean  ：  ピクセル単位による正確なスクロールを有効にするかどうかを指定します（オプション）。
     *    scrollPixel  ：  number  ：  scrollByPixelがtrueの場合に、一度にスクロールするピクセルの数を決定します。 最終的なスクロール量は、スクロールデルタとscrollPixelの乗算結果になります。 たとえば、スクロールデルタが3、scrollPixelが5の場合、スクロール量は15ピクセルになります。
     *    customList  ：  array  ：  ユーザーがドラッグアンドフィルをカスタマイズするためのリスト。各フィル処理でこのリストが優先されます。 各配列型項目は、文字列配列です。
     *    calcOnDemand  ：  boolean  ：  数式が要求されたときにのみ計算するかどうかを指定します（オプション）。
     *    font  ：  string  ：  タブストリップのフォント。
     *    allowUserDeselect  ：  boolean  ：  ユーザーが選択範囲内の選択を解除できるかどうかを指定します（オプション）。
     *    sheetCount  ：  number  ：  シートの数。
     *    allowAutoCreateHyperlink  ：  boolean  ：  シートでハイパーリンクの自動作成を有効にするかどうかを指定します。
     *    scrollbarAppearance  ：  GC.Spread.Sheets.ScrollbarAppearance  ：  skinとmobileの2つの列挙型を含むスクロールバーの外観。 デフォルトはスキンです。
     *    allowDynamicArray  ：  boolean  ：  動的配列を有効にするかどうかを指定します（オプション）。
     *    enableAccessibility  ：  boolean  ：  スプレッドシートでアクセシビリティサポートを有効にするかどうかを指定します。
     *    iterativeCalculation  ：  boolean  ：  反復計算を有効にするかどうかを指定します。
     *    iterativeCalculationMaximumIterations  ：  number  ：  反復計算時の最大反復。
     *    iterativeCalculationMaximumChange  ：  number  ：  反復計算時の最大変更。
     */
    public static get DefaultSpreadOptions() {
        return {
            // タブ関連
            // シート名編集禁止設定

            //【SpreadJSグリッド操作】
            // 名前の変更(ユーザが新規追加したシートに限り、シート名の変更を許容する。)
            //tabEditable: false,
            tabEditable: true,

            // シートタブをドラッグして順序を変更できないように設定
            allowSheetReorder: false,
            // タブナビゲーションの非表示設定
            tabNavigationVisible: false,
            // タブストリップ幅の比率を設定
            //tabStripRatio : 0.5,

            // スクロール関連
            // スクロールバーがアクティブシートの最後の行および列に揃うように設定
            scrollbarMaxAlign: true,
            // 表示されるスクロールバーがシートの全体の列数と行数と揃うように設定
            scrollbarShowMax: true,
            // その他
            // 貼り付け範囲が足りない場合に貼り付け範囲を拡張する
            //allowExtendPasteRange: false,
            allowExtendPasteRange: true,

            //Excelスタイルのコピーと貼り付けを無効にする
            allowCopyPasteExcelStyle: false,
            //ユーザーが範囲をドラッグフィルできるかどうかを指定します（#1282）。
            allowUserDragFill: false,
        }
    }

    /**
     * Spreadのデフォルト設定（全て項目は下記）
     *#5636
     */
    protected get DefaultSpreadOptions() {
        return WorkBookBase.DefaultSpreadOptions;
    }

    /**
      * ワークブックを作成する
      */
    async createWorkBook(container: any): Promise<any> {

        this.container = container;

        return new Promise((resolve, reject) => {

            //実ワークブックの作成
            this.createWorkBookInstatce();

            // レイアウト処理の無効化
            this.rawSpread.suspendPaint();
            // イベント処理の無効化
            this.rawSpread.suspendEvent();

            var sheetLenght = this.sheets.length;

            // #4119 pureSheetsは末尾に追加しておかないとrawSheetがここで追加したシートになってしまう。
            //       また、列設定などを行う関係上、他のシートより先に追加しておく。
            if (this.extentSheets) {
                for (var i = 0; i < this.extentSheets.length; i++) {
                    const extentSheet = this.extentSheets[i];
                    this.rawSpread.addSheet(sheetLenght, extentSheet);
                    extentSheet.visible(false);
                }
            }

            for (var i = 0; i < sheetLenght; i++) {

                let sheetIns = this.sheets[i];

                //var sheet = sheetIns.createSheet(this);
                let sheet = sheetIns.createSheet();

                //bookに追加
                this.rawSpread.addSheet(i, sheet);

                //シートデータ反映処理()
                let g = sheetIns.load();

                //シートのイベントFork
                this.sheetEventFork(sheetIns);

                //promisePoolに登録
                this.promisePool.push(g);

                //コンボボックスの収集などが、この登録の段階で、間に合ってない場合がある
                //（各シートのコンストラクタ等で行わないケース）
                //よって、各シートの「load」無いで、シートのPromiseを保証するように変更（体感loadが遅くなった気がするが...）

                ////Sheetが持つ非同期プロミスも登録する
                ////TODO 検討!（画面を固めてしまうので、並列のまま、投げっぱなしで良ければ登録しない）
                //Array.prototype.push.apply(this.promisePool, sheetIns.miscPromises);

            }


            //並列実行(順序性を担保しないで良いケース)
            Promise.all(
                //
                this.promisePool

            ).then((values) => {

                //完了(成功)
                resolve();

            }).catch((e) => {
                //エラー
                reject();

            }).finally(() => {

                //#3269
                let $cntr = $(_.isString(this.container) ? document.getElementById(this.container) : $(this.container).get(0));
                $cntr.data(WorkBookBase.DATA_KEY, this);

                //コンボボックス等の値の反映が間に合わないケースがあるので
                //リペイント
                //this.rawSpread?.refresh();
                this.rawSpread?.sheets.forEach(s => { s.repaint(); });

                this.rawSpread?.resumeEvent();
                this.rawSpread?.resumePaint();
            });
        });
    }

    /** 
     *  シートのイベントFork
     */
    sheetEventFork(sheetIns: WorkSheetBase) {
        //値の変更をハンドリング
        sheetIns.onNotifyValueChanged = (sheet: WorkSheetBase, colmunName: string, newValue: any) => {
            // シートの変更通知
            this.onNotifyValueChanged?.call(this, sheet, sheet.sheetName, colmunName, newValue);
        };

        //#5736
        //並び替えが行われた
        sheetIns.onNotifySorting = (sheet: WorkSheetBase, args: any) => {
            // ソート実行通知
            this.onNotifySorting?.call(this, sheet, args);
        };

    }

    ///**
    // * シートの変更通知
    // */
    //onSheetValueChange(sheet: WorkSheetBase, colmunName: string, newValue: any) {
    //    //イベント通知
    //    this.onValueChange?.call(this, sheet, sheet.sheetName, colmunName, newValue);
    //}


    /**
     * 実ワークブックの作成
     */
    createWorkBookInstatce(): Spread.Sheets.Workbook {

        const spreadOptions = {
            sheetCount: this.sheets.length
        };

        const modeOptions = {
            newTabVisible: (this.mode == SPIRIT.constant.GridMode.EDIT) ? true : false,
            //#1257 インポートプレビュー画面で行移動ができてしまう
            allowUserDragDrop: (this.mode == SPIRIT.constant.GridMode.EDIT) ? true : false
        }

        //_.defaultsDeep(spreadOptions, this.DefaultSpreadOptions);
        _.defaultsDeep(spreadOptions, _.assign(this.DefaultSpreadOptions, modeOptions, this.options));

        // ワークブックの作成
        var cntr = _.isString(this.container) ? document.getElementById(this.container) : $(this.container).get(0);
        var spread = new Spread.Sheets.Workbook(cntr, spreadOptions);
        //var spread = new Spread.Sheets.Workbook(document.getElementById(this.container), spreadOptions);

        //空シートができるので一旦削除
        spread.clearSheets();
        // 右クリック時のメニュー制御
        //(シートごとの設定にしたいが、切り分けられない!)
        this.setContextMenu(spread);

        this.setWorkBookEvents(spread);

        // スプレッド情報をセット
        this.rawSpread = spread;

        return spread;
    }

    /**
     * Spread本体のイベント設定
     */
    protected setWorkBookEvents(spread: Spread.Sheets.Workbook) {

        // Event
        // F2 イベント
        // F2キーの設定
        const manager = spread.commandManager();

        // F2キー押下時にコールされる処理の設定
        const commandF2 = {
            canUndo: true,
            execute: (context, options, isUndo) => {
                const currentSheet = context.getSheetFromName(options.sheetName);
                if (!currentSheet.isEditing()) {
                    //編集開始
                    currentSheet.startEdit();
                }
            }
        };

        // F2キー(キーコード113)に上記の処理"commandF2"を割り当て
        // 引数詳細(https://docs.grapecity.com/help/this.spread-js/sheets/#SpreadJS~GC.Spread.Commands.CommandManager~register.html)
        manager.register("f2Edit", commandF2, 113, false, false, false, false);

        //【SpreadJSグリッド操作】
        //切り取り（cut）禁ずる
        var cut = manager.setShortcutKey(undefined, Spread.Commands.Key.x, true, false, false, false);


        //キーボードイベントｎインターセプト
        //this.registerDeleteKeyEvent(spread);
        //this.registerPasteKeyEvent(spread);
    }

    /**
     * DeleteKey
     * @param spread
     */
    registerDeleteKeyEvent(spread: Spread.Sheets.Workbook) {

        //------------------------------------
        //①範囲削除を考慮してないので却下
        //------------------------------------
        //let old = GC.Spread.Sheets.Commands.clear.execute;
        //GC.Spread.Sheets.Commands.clear.execute = function (spread, options, isUndo) {
        //    let col = spread.getActiveSheet().getActiveColumnIndex();
        //    if (col === 3) return true;
        //    return old.apply(this, arguments);
        //};



        //------------------------------------
        //②SelectionChangedイベントが発火しなくなるため却下
        //------------------------------------
        //恐らく→   return old.apply(this, arguments);
        //を読んでない為、ただし呼ぶとデフォルト動作になる（範囲でreadonlyのセルをスキップ出来ない）
        //Delete Key イベント
        const getActualRange = function (sel) {
            sel.row = sel.row === -1 ? 0 : sel.row;
            sel.col = sel.col === -1 ? 0 : sel.col;
            return sel;
        }

        const clearRange = function (sheet: Spread.Sheets.Worksheet, sel) {
            sel = getActualRange(sel);
            for (let i = sel.row; i < sel.row + sel.rowCount; i++) {
                //Filter等で消えているのを削除しない
                //見えているセルのみ削除（visibleの物のみ）
                if (sheet.getRowVisible(i)) {
                    for (let j = sel.col; j < sel.col + sel.colCount; j++) {
                        if (sheet.getColumnVisible(j)) {

                            var ws: WorkSheetBase = sheet.tag();
                            if (ws) {
                                var def = ws.columns[j];
                                //定義上readonlyに設定されている場合
                                if (!def.readonly) {
                                    sheet.getCell(i, j).value(null);
                                }
                            } else {
                                sheet.getCell(i, j).value(null);
                            }
                        }
                    }
                }
            }
        }

        const commandDel = {
            canUndo: true,
            execute: (spread, options, isUndo) => {
                var Commands = Spread.Sheets.Commands;
                if (isUndo) {
                    Commands.undoTransaction(spread, options);
                    return true;
                } else {
                    Commands.startTransaction(spread, options);
                    spread.suspendPaint();
                    let sheet = spread.getActiveSheet();
                    let selections = sheet.getSelections();

                    selections.forEach((sel) => {
                        clearRange(sheet, sel);
                    });

                    spread.resumePaint();
                    Commands.endTransaction(spread, options);
                    return true;
                }
            }
        };
        var commandManager = spread.commandManager();
        commandManager.register("Del", commandDel);
        //deleteKey
        commandManager.setShortcutKey("Del", 46, false, false, false, false);
    }

    /**
     * ClipBoardsPasteのインターセプト(やってみた)
     * @param spread
     */
    registerPasteKeyEvent(spread: Spread.Sheets.Workbook) {

        const commandPaste = {
            canUndo: true,
            execute: (spread, options, isUndo) => {
                var Commands = Spread.Sheets.Commands;
                if (isUndo) {
                    Commands.undoTransaction(spread, options);
                    return true;
                } else {
                    Commands.startTransaction(spread, options);
                    spread.suspendPaint();
                    //debugger;
                    //情報が一切ないので、これでは何も出来ない!!
                    spread.resumePaint();
                    Commands.endTransaction(spread, options);
                    return true;
                }
            }
        };
        commandManager.setShortcutKey(undefined, Spread.Commands.Key.v, true, false, false, false);
        var commandManager = spread.commandManager();
        commandManager.register("Paste", commandPaste);
        commandManager.setShortcutKey("Paste", Spread.Commands.Key.v, true, false, false, false);
    }


    /**
     * コンテキストメニュクリック押下時の定
     */
    protected setContextMenu(spread: Spread.Sheets.Workbook) {

        // コンテキストメニューの制御
        //spread.contextMenu.onOpenMenu = this.onOpenContextMenuFork;
        spread.contextMenu.onOpenMenu = this.onOpenContextMenu.bind(this);
    }


    /**
     * コンテキストメニュクリック押下時の処理
     * @param spread
     */
    //protected onOpenContextMenuFork(menuData, itemsDataForShown, hitInfo, spread): boolean {

    //    // 右クリックした箇所を取得
    //    let hit: any = hitInfo;
    //    //共通的な物は、ここに記載でもいいかもね！！！
    //    //return false;
    //    return this.onOpenContextMenu(menuData, itemsDataForShown, hitInfo, spread);
    //}

    /**
     * コンテキストメニュクリック押下時の処理
     * @description
     *	0: {text: "コピー", name: "gc.spread.copy", command: "gc.spread.contextMenu.copy", iconClass: "gc-spread-copy", workArea: "viewportcolHeaderrowHeaderslicercorner"}
     *	1: {text: "切り取り", name: "gc.spread.cut", command: "gc.spread.contextMenu.cut", iconClass: "gc-spread-cut", workArea: "viewportcolHeaderrowHeaderslicercorner"}
     *	2: {text: "貼り付けのオプション:", name: "gc.spread.pasteOptions", iconClass: "gc-spread-pasteOptions", type: "groupHeader", workArea: "viewportcolHeaderrowHeaderslicercorner"}
     *	3: {command: "gc.spread.contextMenu.pasteAll", name: "gc.spread.pasteAll", iconClass: "gc-spread-pasteAll", group: "gc.spread.pasteOptions", text: "すべて", …}
     *	4: {command: "gc.spread.contextMenu.pasteFormula", name: "gc.spread.pasteFormula", iconClass: "gc-spread-pasteFormula", group: "gc.spread.pasteOptions", text: "数式", …}
     *	5: {command: "gc.spread.contextMenu.pasteValues", name: "gc.spread.pasteValues", iconClass: "gc-spread-pasteValues", group: "gc.spread.pasteOptions", text: "値", …}
     *	6: {command: "gc.spread.contextMenu.pasteFormatting", name: "gc.spread.pasteFormatting", iconClass: "gc-spread-pasteFormatting", group: "gc.spread.pasteOptions", text: "書式", …}
     *	7: {command: "gc.spread.contextMenu.pasteValuesFormatting", name: "gc.spread.pasteValuesFormatting", iconClass: "gc-spread-pasteValuesFormatting", group: "gc.spread.pasteOptions", text: "値&書式", …}
     *	8: {command: "gc.spread.contextMenu.pasteFormulaFormatting", name: "gc.spread.pasteFormulaFormatting", iconClass: "gc-spread-pasteFormulaFormatting", group: "gc.spread.pasteOptions", text: "数式&書式", …}
     *	9: {type: "separator"}
     *	10: {text: "数式と値のクリア", command: "gc.spread.contextMenu.clearContents", name: "gc.spread.clearContents", workArea: "viewportcolHeaderrowHeadercorner"}
     *	11: {type: "separator"}
     *	12: {text: "コピーしたセルの挿入", name: "gc.spread.rowHeaderinsertCopiedCells", command: "gc.spread.contextMenu.rowHeaderinsertCopiedCells", workArea: "rowHeader"}
     *	13: {text: "切り取ったセルの挿入", name: "gc.spread.rowHeaderinsertCutCells", command: "gc.spread.contextMenu.rowHeaderinsertCutCells", workArea: "rowHeader"}
     *	14: {text: "コピーしたセルの挿入", name: "gc.spread.colHeaderinsertCopiedCells", command: "gc.spread.contextMenu.colHeaderinsertCopiedCells", workArea: "colHeader"}
     *	15: {text: "切り取ったセルの挿入", name: "gc.spread.colHeaderinsertCutCells", command: "gc.spread.contextMenu.colHeaderinsertCutCells", workArea: "colHeader"}
     *	16: {text: "コピーしたセルの挿入...", name: "gc.spread.insertCopiedCells", subMenu: Array(2), workArea: "viewport"}
     *	17: {text: "切り取ったセルの挿入...", name: "gc.spread.insertCutCells", subMenu: Array(2), workArea: "viewport"}
     *	18: {text: "挿入", name: "gc.spread.insertRows", command: "gc.spread.contextMenu.insertRows", workArea: "rowHeader"}
     *	19: {text: "挿入", name: "gc.spread.insertColumns", command: "gc.spread.contextMenu.insertColumns", workArea: "colHeader"}
     *	20: {text: "削除", name: "gc.spread.deleteRows", command: "gc.spread.contextMenu.deleteRows", workArea: "rowHeader"}
     *	21: {text: "削除", name: "gc.spread.deleteColumns", command: "gc.spread.contextMenu.deleteColumns", workArea: "colHeader"}
     *	22: {type: "separator"}
     *	23: {text: "挿入", name: "gc.spread.tableInsert", subMenu: Array(4), workArea: "table"}
     *	24: {text: "削除", name: "gc.spread.tableDelete", subMenu: Array(2), workArea: "table"}
     *	25: {type: "separator"}
     *	26: {text: "挿入", name: "gc.spread.insertSheet", command: "gc.spread.contextMenu.insertSheet", workArea: "sheetTab"}
     *	27: {text: "削除", name: "gc.spread.deleteSheet", command: "gc.spread.contextMenu.deleteSheet", workArea: "sheetTab"}
     *	28: {type: "separator"}
     *	29: {text: "フィルタリング ", name: "gc.spread.filter", command: "gc.spread.contextMenu.filter", workArea: "viewportcorner"}
     *	30: {text: "ソート", name: "gc.spread.sort", subMenu: Array(2), workArea: "viewportcorner"}
     *	31: {text: "昇順", name: "gc.spread.slicerSortAscend", command: "gc.spread.contextMenu.slicerSortAscend", iconClass: "gc-spread-sortAscend", workArea: "slicer"}
     *	32: {text: "降順", name: "gc.spread.slicerSortDescend", command: "gc.spread.contextMenu.slicerSortDescend", iconClass: "gc-spread-sortDescend", workArea: "slicer"}
     *	33: {type: "separator"}
     *	34: {text: "コメントの挿入", name: "gc.spread.insertComment", command: "gc.spread.contextMenu.insertComment", iconClass: "gc-spread-insertComment", workArea: "viewportcorner"}
     *	35: {text: "コメントの編集", name: "gc.spread.editComment", command: "gc.spread.contextMenu.editComment", iconClass: "gc-spread-editComment", workArea: "viewportcorner"}
     *	36: {text: "コメントの削除", name: "gc.spread.deleteComment", command: "gc.spread.contextMenu.deleteComment", iconClass: "gc-spread-deleteComment", workArea: "viewportcorner"}
     *	37: {text: "コメントの表示/非表示", name: "gc.spread.toggleComment", command: "gc.spread.contextMenu.toggleComment", workArea: "viewportcorner"}
     *	38: {type: "separator"}
     *	39: {text: "非表示", name: "gc.spread.hideRows", command: "gc.spread.contextMenu.hideRows", workArea: "rowHeader"}
     *	40: {text: "再表示", name: "gc.spread.unhideRows", command: "gc.spread.contextMenu.unhideRows", workArea: "rowHeader"}
     *	41: {text: "非表示", name: "gc.spread.hideColumns", command: "gc.spread.contextMenu.hideColumns", workArea: "colHeader"}
     *	42: {text: "再表示", name: "gc.spread.unhideColumns", command: "gc.spread.contextMenu.unhideColumns", workArea: "colHeader"}
     *	43: {type: "separator"}
     *	44: {text: "非表示", name: "gc.spread.hideSheet", command: "gc.spread.contextMenu.hideSheet", workArea: "sheetTab"}
     *	45: {text: "再表示", name: "gc.spread.unhideSheet", command: "gc.spread.contextMenu.unhideSheet", workArea: "sheetTab"}
     *	46: {type: "separator"}
     *	47: {text: "削除", name: "gc.spread.removeSlicer", command: "gc.spread.contextMenu.removeSlicer", workArea: "slicer"}
     * @param spread
     */
    //protected abstract onOpenContextMenu(menuData, itemsDataForShown, hitInfo, spread): boolean;
    protected onOpenContextMenu(menuData, itemsDataForShown: any[], hitInfo, spread): boolean {

        // 右クリックした箇所を取得
        let hit: any = hitInfo;

        let sheet = spread.getActiveSheet();
        //タグにworkSheetのインスタンスを隠しているので取得する
        let workSheet: WorkSheetBase = sheet.tag();

        // タブを右クリックしたときの処理
        if (hit.tabStripHitInfo) {
            //各Bookで実装してください
            //overrideしたうえで下記を呼ぶ
            //super.onOpenContextMenu(menuData, itemsDataForShown, hitInfo, spread);

            //if (hit.tabStripHitInfo.sheetTab.sheetName == "プラントグループ") {
            //    //メモ: IE8 では、deleteCount が省略された場合はすべての要素を削除しません。
            //    //itemsDataForShown.splice(0);
            //    itemsDataForShown.splice(0, itemsDataForShown.length);
            //} else {
            //    //新規追加したTabのみ削除可能とする
            //    //削除以外、除去
            //    //{text: "削除", name: "gc.spread.deleteSheet", command: "gc.spread.contextMenu.deleteSheet", workArea: "sheetTab"}
            //    //下記はダメ！参照を意識し配列その物を変更する必要がある
            //    //itemsDataForShown = itemsDataForShown.filter(item => item.name === 'gc.spread.deleteSheet');
            //    itemsDataForShown.splice(1, 5);
            //}


        }

        //一覧の列ヘッダ、行ヘッダ、セルを選択し、右クリック
        //表示する内容はコピー、切り取り、挿入、削除とする。貼り付けのオプションは不要。
        if (hit.worksheetHitInfo) {
            //編集モードでない場合
            if (this.mode != SPIRIT.constant.GridMode.EDIT) {
                //持っていない場合、無効
                itemsDataForShown.splice(0, itemsDataForShown.length);
                return false;
            }

            if (hit.worksheetHitInfo.hitTestType === 0) {
                //左上コーナー
                //何もしない
                itemsDataForShown.splice(0, itemsDataForShown.length);

            } else if (hit.worksheetHitInfo.hitTestType === 1) {
                //列ヘッダークリック
                //【SpreadJSグリッド操作】
                //コピーのみ
                var filterd = itemsDataForShown.filter(item =>
                    //{ text: "コピー", name: "gc.spread.copy", command: "gc.spread.contextMenu.copy", iconClass: "gc-spread-copy", workArea: "viewportcolHeaderrowHeaderslicercorner" }
                    item.name === 'gc.spread.copy'
                );
                itemsDataForShown.splice(0, itemsDataForShown.length);
                Array.prototype.push.apply(itemsDataForShown, filterd);

                //} else if (hit.worksheetHitInfo.colViewportIndex == -1) {
            } else if (hit.worksheetHitInfo.hitTestType === 2) {
                //行ヘッダークリック
                //debugger;
                //【SpreadJSグリッド操作】
                //対象を引っこ抜いて、再度投入(配列の参照をそのまま変更する必要がある為)
                var filterd = itemsDataForShown.filter(item => {

                    // #4328 行ヘッダーのコンテキストメニューに「コピー」を表示させない
                    if (item.name === 'gc.spread.copy' && this.doHideCopyAndPastingContextMenuAtRowHeader) {
                        return false;
                    }

                    if (!workSheet?.isNeedExtraRow) {
                        //固定列
                        //{ text: "コピー", name: "gc.spread.copy", command: "gc.spread.contextMenu.copy", iconClass: "gc-spread-copy", workArea: "viewportcolHeaderrowHeaderslicercorner" }
                        return item.name === 'gc.spread.copy'
                    } else {
                        //通常
                        //{ text: "コピー", name: "gc.spread.copy", command: "gc.spread.contextMenu.copy", iconClass: "gc-spread-copy", workArea: "viewportcolHeaderrowHeaderslicercorner" }
                        //{text: "挿入", name: "gc.spread.insertRows", command: "gc.spread.contextMenu.insertRows", workArea: "rowHeader"}
                        //{text: "削除", name: "gc.spread.deleteRows", command: "gc.spread.contextMenu.deleteRows", workArea: "rowHeader"}
                        return item.name === 'gc.spread.copy' || item.name === 'gc.spread.insertRows' || item.name === 'gc.spread.deleteRows'
                    }
                });

                //貼り付け(値のみ)
                //filterd.push({ text: "貼り付けのオプション:", name: "gc.spread.pasteOptions", iconClass: "gc-spread-pasteOptions", type: "groupHeader", workArea: "viewportcolHeaderrowHeaderslicercorner" })
                //filterd.push({ command: "gc.spread.contextMenu.pasteValues", name: "gc.spread.pasteValues", iconClass: "gc-spread-pasteValues", group: "gc.spread.pasteOptions", text: "値" })
                // コンテキストメニュー「貼付け」を非表示にするか否か
                if (!this.doHideCopyAndPastingContextMenuAtRowHeader) {
                    filterd.push({ text: "貼り付け", name: "gc.spread.pasteValues", command: "gc.spread.contextMenu.pasteValues", iconClass: "gc-spread-pasteOptions", workArea: "viewportcolHeaderrowHeaderslicercorner", disable: this.disabled });
                }
                itemsDataForShown.splice(0, itemsDataForShown.length);
                Array.prototype.push.apply(itemsDataForShown, filterd);


            } else {
                //セルクリック（挿入って無い？）

                //【SpreadJSグリッド操作】
                //コピー
                var filterd = itemsDataForShown.filter(item =>
                    //{ text: "コピー", name: "gc.spread.copy", command: "gc.spread.contextMenu.copy", iconClass: "gc-spread-copy", workArea: "viewportcolHeaderrowHeaderslicercorner" }
                    item.name === 'gc.spread.copy'
                );
                //貼り付け(値のみ)
                //filterd.push({ text: "貼り付けのオプション:", name: "gc.spread.pasteOptions", iconClass: "gc-spread-pasteOptions", type: "groupHeader", workArea: "viewportcolHeaderrowHeaderslicercorner" })
                //filterd.push({ command: "gc.spread.contextMenu.pasteValues", name: "gc.spread.pasteValues", iconClass: "gc-spread-pasteValues", group: "gc.spread.pasteOptions", text: "値" })
                filterd.push({ text: "貼り付け", name: "gc.spread.pasteValues", command: "gc.spread.contextMenu.pasteValues", iconClass: "gc-spread-pasteOptions", workArea: "viewportcolHeaderrowHeaderslicercorner" });

                itemsDataForShown.splice(0, itemsDataForShown.length);
                Array.prototype.push.apply(itemsDataForShown, filterd);
            }
        }

        //itemsDataForShownを変更することで、フィルタ結果を変更できます。
        //フィルタ結果を変更するだけの場合は、falseを返すか、何も返さないでください。
        //また、独自のコンテキストメニューを開くこともできます。その場合はtrueを返します。
        return false;
    }



    /**
     * データを再取得しなおす
     * @param data
     */
    reload(sheetName?: string) {
        if (sheetName) {
            this.getSheet(sheetName).reload();
        } else {
            this.sheets.forEach((sheet, i, all) => {
                sheet.reload();
            });
        }
    }

    /**
     * 描画のリフレッシュ
     */
    refresh() {
        this.getSpread()?.refresh();
    }


























    /**
     * Spread情報
     */
    public getSpreadInfo() {
        // TODO リファクタ STUB ? 
        let sheet = this.getActiveSheet();
        let sheetInfo = sheet.getSheetInfo();
        return {
            spread: this.rawSpread,
            spreadName: sheetInfo.sheetName,
            colInfos: sheetInfo.columns
        }
    }

    /**
     * Spreadの現在の状態からJSONを取得
     * 
     * @return {Object} 
     */
    getSpreadToJSON() {
        //TODO リファクタ
        return this.rawSpread.toJSON({ includeBindingSource: true, columnHeadersAsFrozenRows: true });
    }


    /**
    * 保存データ取得
    * 
    * @return {Object} result
    * @return {Object} result.addData 追加データ
    * @return {Object} result.changeData 変更データ
    * @return {Object} result.deleteData 削除データ
    */
    getSaveData(sheetName?: string): any {

        SPIRIT.dialog.MessgeDialog('注意!!', 'この処理はもう古いソースです！カテゴリ情報を参考に修正してください！', null);


        let sheet = (sheetName) ? this.getSheet(sheetName) : this.getActiveSheet();
        // TODO リファクタ 
        const dataSource = sheet.getDataSource();

        // エラー背景色解除
        // 全セルを対応するとまずいか？エラー情報を退避させ、その情報で処理するか？
        // 一旦全セルを対応とし、パフォーマンスに問題があれば、エラー箇所のみ処理するように変更する。
        //this.spread.suspendPaint();
        //const rowCount = this.sheet.getRowCount();
        //const colCount = this.sheet.getColumnCount();
        //for (let i = 0; i <= rowCount; i++) {
        //    for (let j = 1; j <= colCount; j++) {
        //        this.sheet.getCell(i, j).backColor('');
        //    }
        //}
        //this.spread.resumePaint();

        this.rawSpread.suspendPaint();
        const rowCount = sheet.getRowCount();
        const colCount = sheet.getColumnCount();
        sheet.rawSheet.getRange(0, 0, rowCount, colCount).backColor('');

        this.rawSpread.resumePaint();

        // TODO クライアントバリデーション(ここではやらない!!)

        // TODO 保存データのみに絞る
        const result = {
            addData: dataSource,
            changeData: dataSource,
            deleteData: [],
        };

        return result;
    }




    /**
    * 配列内のObjectを比較し、差分があったObjectの配列のみ返却
    * @param src 比較元
    * @param dst 比較先
    */
    // 共通にできるか検討が必要なため一旦呼ばないでください。
    protected getDiff(src, dst, customComparator) {

        //let arrayA = [{ 'id': 1, 'name': '花子' }, { 'id': 2, 'name': '太郎' }, { 'id': 3, 'name': '次郎' }];
        //let arrayB = [{ 'id': 1, 'name': '花子' }];

        //_.differenceWith(arrayA, arrayB, _.isEqual)
        //// [{"id":2,"name":"太郎"},{"id":3,"name":"次郎"}]

        //// こんな感じでも指定できる
        //const customComparator = (a, b) => {
        //    return a.id === b.id && a.name === b.name
        //}
        //_.differenceWith(arrayA, arrayB, customComparator)
        //// [{"id":2,"name":"太郎"},{"id":3,"name":"次郎"}]

        console.log(src);
        console.log(dst);
        // これだとプロパティ数も一致する必要があるので、項目を絞って比較しないと
        // spreadjsの操作でなくなるプロパティ(列定義されていないもの)があるのでダメ。
        //const diff = _.differenceWith(src, dst, _.isEqual);
        const diff = _.differenceWith(src, dst, customComparator);



        console.log(diff);

        // TODO 配列の差は確認できることを確認。
        // もう少し使いやすいように処理も検討が必要。
        // その前にデータソースの確立を優先するため一旦ここまでとする。

        // 共通でなく個別でやってもらうか？

        return diff;
    }

    /**
    * 現在の状態で表示順を設定
    * @param list 現在のGridの状態の配列
    */
    // 共通にできるか検討が必要なため一旦呼ばないでください。
    protected SetOrderNo(list: any[]) {

        //// 名前降順
        //_.orderBy(members, 'name', 'desc'); // => membersが書き換わる

        //// 名前の長さ昇順
        //_.orderBy(members, (item) => {
        //    return item.name.length;
        //}, 'desc');

        //// 名前昇順・作成日降順（名前のほうが優先）
        //_.orderBy(members, ['name', 'created_at'], ['asc', 'desc']);

        // sortBy / orderBy 
        // 関数あるいはプロパティ名で並び替え
        // orderByの方が、昇順降順が選べる
        // 破壊的（元の配列の順番を変えてしまう）なので注意

        // 破壊的のためクローン
        const tmpList = _.cloneDeep(list);



        // 現在の順番でorderNoを変更
        tmpList.forEach((item, index) => {
            const orderNo = index + 1;
            item.OrderNo = orderNo;
        });

        // TODO グループ毎の順番を設定する必要がある

        // 共通でなく個別でやってもらうか？

        return tmpList;
    }


    /**
     * Exportするファイルを作成し、コールバックにSpreadのJSONを返却
     * ※一覧形式のみ対応。マトリックス表現するようなものは個別で実装してください！！！
     * @param url データ取得URL
     * @param sheetName シート名
     * @param colInfos カラム情報（取込区分、システムカラムは除く）
     * @param idColName ID列名
     * @param hasImportUserAuth インポート権限を持つユーザか（trueの場合、取込区分がつく）
     * @param cb ダウンロード処理関数
     */
    protected exportSpreadJsonForMaster(url: string, sheetName: string, colInfos, idColName: string, hasImportUserAuth: boolean, cb) {

        // TODO リファクタ 
        SPIRIT.util.getData(url).then((data) => {

            // Spread作成
            const spread = new Spread.Sheets.Workbook(null, { sheetCount: 1 });

            spread.suspendPaint();

            const sheet = spread.getActiveSheet();

            sheet.name(sheetName);
            sheet.autoGenerateColumns = false;
            sheet.setDataSource(data);

            // 取込区分
            const firstFixColInfos = [{ name: 'ImportType', displayName: '取込区分', size: 80 }];
            const firstConcatColInfos = hasImportUserAuth ? firstFixColInfos.concat(colInfos) : colInfos;

            // システムカラム
            const lastColInfos = [
                { name: 'UpdateUserName', displayName: '更新ユーザ名', size: 150 },
                { name: 'UpdateUserLoginId', displayName: '更新ユーザID', size: 100 },
                { name: 'UpdateDateString', displayName: '更新日時', size: 200 },
            ];
            const concatColInfos = firstConcatColInfos.concat(lastColInfos);

            sheet.bindColumns(concatColInfos);

            // 列スタイル設定
            concatColInfos.forEach((item, index) => {

                // 取込区分列のリスト設定
                if (hasImportUserAuth && item.name == 'ImportType') {
                    const dv = Spread.Sheets.DataValidation.createListValidator('追加, 変更, 削除');
                    dv.showErrorMessage(true);
                    // ヘッダ行も設定される（行数指定がないので本来はこうするべき）
                    //sheet.setDataValidator(-1, index, dv, Spread.Sheets.SheetArea.viewport);
                    // ヘッダ行が設定されない方法（行数指定が必要：一旦Excel最大行数は1048576なのでヘッダ数を引いて1048575にする）
                    sheet.setDataValidator(0, 0, 1048575, 1, dv, Spread.Sheets.SheetArea.viewport);
                }

                // 列罫線
                const style = new Spread.Sheets.Style();
                style.borderTop = new Spread.Sheets.LineBorder('#000000', Spread.Sheets.LineStyle.thin);
                style.borderBottom = new Spread.Sheets.LineBorder('#000000', Spread.Sheets.LineStyle.thin);
                style.borderLeft = new Spread.Sheets.LineBorder('#000000', Spread.Sheets.LineStyle.thin);
                style.borderRight = new Spread.Sheets.LineBorder('#000000', Spread.Sheets.LineStyle.thin);

                // ID列の背景色指定
                if (hasImportUserAuth && item.name == idColName) {
                    style.backColor = '#FFFF00';
                } else {
                    // ヘッダー背景色指定
                    const colHeader = sheet.getRange(0, index, 1, 1, Spread.Sheets.SheetArea.colHeader);
                    colHeader.backColor('#E8E8E8');
                }

                sheet.setStyle(-1, index, style, Spread.Sheets.SheetArea.viewport);
            });

            spread.resumePaint();

            const json = spread.toJSON({ includeBindingSource: true, columnHeadersAsFrozenRows: true });

            if (cb != null) {
                cb(json);
            }
        });

    }






















    /**
     * 
     * @param columns
     * @param data
     * @param option
     */
    static toExcel(data: any, filename, columns?: [], option?) {

        //let spreadOption = {
        //    sheetCount: this.sheetSettings.length,
        //    allowExtendPasteRange: true,
        //    allowCopyPasteExcelStyle: false,
        //    newTabVisible: (this.gridMode == SPIRIT.constant.GridMode.EDIT) ? true : false
        //}

        let spreadOption = {};
        spreadOption = _.assign(spreadOption, option);
        // ワークブックの作成
        var spread = new Spread.Sheets.Workbook(null, spreadOption);
        var s = spread.getActiveSheet()
        if (columns && columns.length > 0) {
            s.autoGenerateColumns = false;
            s.bindColumns(columns);
        } else {
            s.autoGenerateColumns = true;
        }
        s.setDataSource(data);


        WorkBookBase.exportExcelFile(filename, spread);
    }


    /**
     * 
     * @param filename
     * @param spread
     */
    private static exportExcelFile(filename: string, spread) {
        var excelIO = new Excel.IO();

        console.time("func"); // 計測開始

        var json = spread.toJSON({
            //ignoreStyle: false,
            // データを出力
            includeBindingSource: true,
            // ヘッダを出力
            //columnHeadersAsFrozenRows: true
        });

        //console.timeLog("func"); // 必要に応じて
        var fileName = filename + new Date().valueOf() + '.xlsx'

        // here is excel IO API(for browser)
        excelIO.save(json, function (blob) {
            console.timeEnd("func"); // 計測終了
            saveAs(blob, fileName);
            console.log("Export success");
        }, function (e) {
            // process error
            console.log(e);
            // }, {password: password});
        });


        // here is excel IO API(for NodeJs)
        //excelIO.save(json, (data) => {
        //    console.timeEnd("func"); // 計測終了
        //    //fs.appendFileSync(fileName, new Uint8Array(data), function (err) {//OK
        //    //    //fs.appendFileSync(fileName, new Buffer(data), function (err) { //なんか駄目
        //    //    //fs.appendFileSync(fileName, data, function (err) {
        //    //    console.log(err);
        //    //});
        //    console.log("Export success");
        //}, (err) => {
        //    console.log(err);
        //}, { useArrayBuffer: true });

    }



}
